// src/App.tsx
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route,Navigate } from "react-router-dom";
import { uiLogger } from "./utils/frontendLogger";
import { Clock } from "lucide-react";
//import { Outlet } from "react-router-dom";


/* ── Eager: shell, route guards, providers, and the public entry page.
   These are needed on every route, so they stay in the main bundle. ── */
import LandingPage from "./pages/LandingPage";
import DashboardLayout from "./layouts/DashboardLayout";
import PlatformLayout from "./layouts/PlatformLayout";
import ResidentLayout from "./layouts/ResidentLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import RoleProtectedRoute from "./routes/RoleProtectedRoute";
import PlatformProtectedRoute from "./components/platform/PlatformProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { ToastProvider } from "./context/ToastContext";
import { FormDraftProvider } from "./context/FormDraftContext";

/* ── Lazy: every page is split into its own chunk, downloaded on demand.
   This keeps the initial bundle to the shell + landing page, and pulls heavy
   page-level libs (jspdf, xlsx, html2canvas, recharts) out of the main bundle. ── */
/* Auth */
const AuthGateway = lazy(() => import("./pages/AuthGateway"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PaymentTerms = lazy(() => import("./pages/PaymentTerms"));
const PaymentPrivacy = lazy(() => import("./pages/PaymentPrivacy"));

/* Dashboard */
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CollectionsDashboard = lazy(() => import("./pages/CollectionsDashboard"));

/* Blocks */
const Blocks = lazy(() => import("./pages/Blocks"));
const BlockCreate = lazy(() => import("./pages/BlockCreate"));
const BlockView = lazy(() => import("./pages/BlockView"));
const BlockEdit = lazy(() => import("./pages/BlockEdit"));

/* Flats */
const Flats = lazy(() => import("./pages/Flats"));
const FlatCreate = lazy(() => import("./pages/FlatCreate"));
const FlatView = lazy(() => import("./pages/FlatView"));
const FlatEdit = lazy(() => import("./pages/FlatEdit"));
const MeterNumberPage = lazy(() => import("./pages/MeterNumberPage"));

const WaterBillingRules = lazy(() => import("./pages/WaterBillingRules"));

/* Owners */
const OwnerList = lazy(() => import("./pages/OwnerList"));
const OwnerCreate = lazy(() => import("./pages/OwnerCreate"));
const OwnerView = lazy(() => import("./pages/OwnerView"));
const OwnerEdit = lazy(() => import("./pages/OwnerEdit"));
const OwnerAssignFlats = lazy(() => import("./pages/OwnerAssignFlats"));

/* Residents */
const ResidentsList = lazy(() => import("./pages/ResidentsList"));
const ResidentCreate = lazy(() => import("./pages/ResidentCreate"));
const ResidentView = lazy(() => import("./pages/ResidentView"));
const ResidentEdit = lazy(() => import("./pages/ResidentEdit"));

/* Suppliers */
const Suppliers = lazy(() => import("./pages/Suppliers"));
const SupplierCreate = lazy(() => import("./pages/SupplierCreate"));
const SupplierView = lazy(() => import("./pages/SupplierView"));
const SupplierEdit = lazy(() => import("./pages/SupplierEdit"));

/* Water */
const WaterPurchases = lazy(() => import("./pages/WaterPurchases"));
const WaterPurchaseCreate = lazy(() => import("./pages/WaterPurchaseCreate"));
const WaterPurchaseView = lazy(() => import("./pages/WaterPurchaseView"));
const WaterPurchaseEdit = lazy(() => import("./pages/WaterPurchaseEdit"));
const WaterPurchaseEntryReview = lazy(() => import("./pages/WaterPurchaseEntryReview"));
const WaterConsumption = lazy(() => import("./pages/WaterConsumption"));
const WaterConsumptionForm = lazy(() => import("./pages/WaterConsumptionForm"));
const WaterConsumptionView = lazy(() => import("./pages/WaterConsumptionView"));
const WaterConsumptionUpload = lazy(() => import("./pages/WaterConsumptionUpload"));
const WaterBillingHub = lazy(() => import("./pages/WaterBillingHub"));
const WaterBillForm = lazy(() => import("./pages/WaterBillForm"));

/* Billing */
const WaterSplitCockpit = lazy(() => import("./pages/WaterSplitCockpit"));
const WaterBillingReport = lazy(() => import("./pages/WaterBillingReport"));
const WaterBillingSummaryPage = lazy(() => import("./pages/WaterBillingSummaryPage"));
const ElectricityBillList = lazy(() => import("./pages/ElectricityBillList"));
const ElectricityBillForm = lazy(() => import("./pages/ElectricityBillForm"));
const ElectricityBillView = lazy(() => import("./pages/ElectricityBillView"));
const Expenditures = lazy(() => import("./pages/Expenditures"));
const ExpenditureForm = lazy(() => import("./pages/ExpenditureForm"));
const BudgetSettings = lazy(() => import("./pages/BudgetSettings"));
const OutstandingWriteOff = lazy(() => import("./pages/OutstandingWriteOff"));
const OpeningBalances = lazy(() => import("./pages/OpeningBalances"));
// Release gate: build with REACT_APP_ENABLE_OUTSTANDING_WRITEOFF=true (and run the API with ENABLE_OUTSTANDING_WRITEOFF=true) to expose it.
const OUTSTANDING_WRITEOFF_ENABLED = process.env.REACT_APP_ENABLE_OUTSTANDING_WRITEOFF === "true";
const RecurringExpenseForm = lazy(() => import("./pages/RecurringExpenseForm"));
const MasterDataManagementPage = lazy(() => import("./pages/MasterDataManagementPage"));
const BillingRules = lazy(() => import("./pages/BillingRules"));
const BillingRulesList = lazy(() => import("./pages/BillingRulesList"));

/* Bill Statements */
const BillingSummaryPage = lazy(() => import("./pages/BillingSummaryPage"));
const BillStatementView = lazy(() => import("./pages/BillStatementView"));
const PublicPaymentPage = lazy(() => import("./pages/PublicPaymentPage"));
const PublicStatementPage = lazy(() => import("./pages/PublicStatementPage"));
const WaterApprovalPage = lazy(() => import("./pages/WaterApprovalPage"));

/* Payments */
const Payments = lazy(() => import("./pages/Payments"));
const PaymentUpload = lazy(() => import("./pages/PaymentUpload"));
const PaymentView = lazy(() => import("./pages/PaymentView"));
const PaymentEdit = lazy(() => import("./pages/PaymentEdit"));
const Receipts = lazy(() => import("./pages/Receipts"));
const ReceiptsView = lazy(() => import("./pages/ReceiptsView"));
const ReceiptCreate = lazy(() => import("./pages/ReceiptCreate"));
const LedgerReport = lazy(() => import("./pages/LedgerReport"));
const FinancialHealthDashboard = lazy(() => import("./pages/reports/financial-health"));

/* Staff */
const Staff = lazy(() => import("./pages/Staff"));
const StaffCreate = lazy(() => import("./pages/StaffCreate"));

/* Notices, growth, system */
const Notices = lazy(() => import("./pages/Notices"));
const NoticeCreate = lazy(() => import("./pages/NoticeCreate"));
const VacancyHub = lazy(() => import("./pages/VacancyHub"));
const LeadManagement = lazy(() => import("./pages/LeadManagement"));
const LeadCreate = lazy(() => import("./pages/LeadCreate"));
const CreditNotes = lazy(() => import("./pages/CreditNotes"));
const SmartImportStudio = lazy(() => import("./pages/SmartImportStudio"));
const ImportSummaryPage = lazy(() => import("./pages/ImportSummaryPage"));
const ImportHistoryPage = lazy(() => import("./pages/ImportHistoryPage"));
const CommunicationLogs = lazy(() => import("./pages/CommunicationLogs"));

/* Resident portal */
const ResidentPortal = lazy(() => import("./pages/ResidentPortal"));
const ResidentBills = lazy(() => import("./pages/ResidentBills"));
const ResidentPayments = lazy(() => import("./pages/ResidentPayments"));
const ResidentNotices = lazy(() => import("./pages/ResidentNotices"));
const ActivateAccount = lazy(() => import("./pages/ActivateAccount"));
const ResidentOnboarding = lazy(() => import("./pages/ResidentOnboarding"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ResidentProfile = lazy(() => import("./pages/ResidentProfile"));
const ResidentStatements = lazy(() => import("./pages/ResidentStatements"));

/* Settings */
const EmailSettings = lazy(() => import("./pages/EmailSettings"));
const RolesPermissions = lazy(() => import("./pages/RolesPermissions"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const WhatsAppSettings = lazy(() => import("./pages/WhatsAppSettings"));
const BillingConfiguration = lazy(() => import("./pages/BillingConfiguration"));
const NotificationPreferences = lazy(() => import("./pages/NotificationPreferences"));
const PaymentActivation = lazy(() => import("./pages/PaymentActivation"));
const PlatformRevenueDashboard = lazy(() => import("./pages/PlatformRevenueDashboard"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));

/* Treasury */
const TreasuryDashboard = lazy(() => import("./pages/treasury/TreasuryDashboard"));
const TreasuryAccounts = lazy(() => import("./pages/treasury/TreasuryAccounts"));
const TreasuryBankAccounts = lazy(() => import("./pages/treasury/TreasuryBankAccounts"));
const BankAccountFormPage = lazy(() => import("./pages/treasury/BankAccountFormPage"));
const TreasuryCashInHand = lazy(() => import("./pages/treasury/TreasuryCashInHand"));
const CashAccountFormPage = lazy(() => import("./pages/treasury/CashAccountFormPage"));
const TreasuryFunds = lazy(() => import("./pages/treasury/TreasuryFunds"));
const FundFormPage = lazy(() => import("./pages/treasury/FundFormPage"));
const CashbookPage = lazy(() => import("./pages/treasury/CashbookPage"));
const AccountStatementsPage = lazy(() => import("./pages/treasury/AccountStatementsPage"));
const VerificationReportPage = lazy(() => import("./pages/treasury/VerificationReportPage"));
const VerificationWizardPage = lazy(() => import("./pages/treasury/VerificationWizardPage"));
const BankReconciliationPage = lazy(() => import("./pages/treasury/BankReconciliationPage"));
const BankReconciliationDetailPage = lazy(() => import("./pages/treasury/BankReconciliationDetailPage"));
const BalanceSheetPage = lazy(() => import("./pages/treasury/BalanceSheetPage"));

/* Platform (super-admin) */
const PlatformDashboard = lazy(() => import("./pages/platform/Dashboard"));
const TenantRegistry = lazy(() => import("./pages/platform/TenantRegistry"));
const TenantDrilldown = lazy(() => import("./pages/platform/TenantDrilldown"));
const SecurityIntelligence = lazy(() => import("./pages/platform/SecurityIntelligence"));
const PlatformLogin = lazy(() => import("./pages/platform/Login"));
const EventMonitor = lazy(() => import("./pages/platform/EventMonitor"));
const AnomalyCenter = lazy(() => import("./pages/platform/AnomalyCenter"));
const PlatformSettings = lazy(() => import("./pages/platform/Settings"));
const SubscriptionManagement = lazy(() => import("./pages/platform/SubscriptionManagement"));
const PortfolioHealth = lazy(() => import("./pages/platform/PortfolioHealth"));
const AIInsights = lazy(() => import("./pages/platform/AIInsights"));
const AuditTrail = lazy(() => import("./pages/platform/AuditTrail"));
const PermissionCenter = lazy(() => import("./pages/platform/PermissionCenter"));
const ReportsHub = lazy(() => import("./pages/platform/ReportsHub"));

/* Mobile */
const MobileScanPage = lazy(() => import("./pages/MobileScanPage"));

/* Lazy-loaded routes */
const FinancialAuditPage = lazy(() =>
  import("./pages/treasury/FinancialAuditPage").then(m => ({ default: m.FinancialAuditPage }))
);

// Skeleton shown while FinancialAuditPage chunk downloads
const AuditPageSkeleton = () => (
  <div className="px-4 md:px-6 pt-4 pb-16 space-y-6 animate-pulse">
    <div className="border-b border-slate-100 pb-5 pt-4 space-y-2">
      <div className="h-3 w-28 bg-slate-200 rounded-full" />
      <div className="h-6 w-52 bg-slate-200 rounded-xl" />
      <div className="h-3 w-36 bg-slate-100 rounded-full" />
    </div>
    <div className="h-14 w-full bg-slate-100 rounded-2xl" />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[0,1,2,3].map(i => (
        <div key={i} className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm space-y-3">
          <div className="h-2.5 w-20 bg-slate-100 rounded-full" />
          {i === 0
            ? <div className="w-20 h-20 rounded-full bg-slate-100 mx-auto" />
            : <div className="h-6 w-24 bg-slate-200 rounded-lg" />
          }
          <div className="h-2.5 bg-slate-100 rounded-full" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-4">
        <div className="h-8 bg-rose-50 border border-rose-100 rounded-xl" />
        {[0,1].map(i => (
          <div key={i} className="border border-slate-100 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 bg-slate-50/50 flex justify-between">
              <div className="space-y-1.5">
                <div className="h-3 w-36 bg-slate-200 rounded-full" />
                <div className="h-2.5 w-24 bg-slate-100 rounded-full" />
              </div>
              <div className="h-5 w-20 bg-slate-100 rounded-full" />
            </div>
          </div>
        ))}
        <div className="h-8 bg-emerald-50 border border-emerald-100 rounded-xl" />
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm space-y-3">
          {[0,1,2,3,4].map(i => (
            <div key={i} className="flex items-center gap-3 py-1.5">
              <div className="w-4 h-4 rounded-full bg-emerald-100 shrink-0" />
              <div className="h-2.5 flex-1 bg-slate-100 rounded-full" />
              <div className="h-2.5 w-20 bg-slate-100 rounded-full" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-4">
        <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm space-y-3">
          <div className="h-3 w-36 bg-slate-200 rounded-full" />
          <div className="h-16 bg-slate-100 rounded-xl" />
          {[0,1,2,3].map(i => <div key={i} className="h-8 bg-slate-50 rounded-xl" />)}
          <div className="h-10 bg-slate-100 rounded-xl" />
        </div>
        <div className="bg-white border border-slate-100 rounded-3xl p-5 shadow-sm space-y-3">
          <div className="h-3 w-28 bg-slate-200 rounded-full" />
          {[0,1,2].map(i => <div key={i} className="h-2.5 bg-slate-100 rounded-full" />)}
        </div>
      </div>
    </div>
  </div>
);

/* Fallback shown while a route chunk downloads (public/auth pages). Layout
   routes use a nested Suspense around their Outlet so the shell stays visible. */
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

/* Placeholder */
const ComingSoon = () => (
  <div className="flex flex-col items-center justify-center p-20 min-h-[60vh] space-y-6">
    <div className="p-6 bg-blue-50 text-blue-600 rounded-[2rem] shadow-inner">
      <Clock size={48} className="animate-pulse" />
    </div>
    <div className="text-center space-y-2">
      <h2 className="text-3xl font-black text-gray-900 tracking-tight">Coming Soon</h2>
      <p className="text-gray-500 font-medium max-w-xs mx-auto">
        We're building this feature to make your community management effortless. Stay tuned!
      </p>
    </div>
    <button 
      onClick={() => window.history.back()}
      className="btn-secondary"
    >
      Go Back
    </button>
  </div>
);

function App() {
  /* ── Global error listeners — pipe to uiLogger for production debugging ── */
  useEffect(() => {
    const onUnhandledRejection = (e: PromiseRejectionEvent) => {
      uiLogger.error("Unhandled promise rejection", {
        reason: String(e.reason),
        stack:  e.reason?.stack ?? "",
      });
    };
    const onError = (e: ErrorEvent) => {
      uiLogger.error("Uncaught JS error", {
        message:  e.message,
        filename: e.filename,
        lineno:   e.lineno,
        colno:    e.colno,
      });
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onError);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onError);
    };
  }, []);

  return (
    <ToastProvider>
      <FormDraftProvider>
      <Router>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public Payment Flow (Top Priority - No Auth Required) */}
          <Route path="/public/pay/:token" element={<PublicPaymentPage />} />
          <Route path="/public/statement/:token" element={<PublicStatementPage />} />

          {/* Email approval of guard-submitted water deliveries (token-authorised, no login) */}
          <Route path="/water-approval/:token" element={<WaterApprovalPage />} />

          {/* Resident Account Activation (No Auth Required) */}
          <Route path="/activate/:token" element={<ActivateAccount />} />

          {/* Resident Self-Onboarding (No Auth Required) */}
          <Route path="/onboard/:token" element={<ResidentOnboarding />} />

          {/* Password Recovery (No Auth Required) */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Mobile Scan Flow (No Auth Required - Token Based) */}
          <Route path="/mobile/scan/:sessionId" element={<MobileScanPage />} />

          {/* Auth */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthGateway />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/payment-terms" element={<PaymentTerms />} />
          <Route path="/payment-privacy" element={<PaymentPrivacy />} />

          {/* 🏛️ PLATFORM ROUTES (SUPER_ADMIN ONLY) */}
          <Route path="/platform/login" element={<PlatformLogin />} />
          <Route 
            path="/platform" 
            element={
              <PlatformProtectedRoute>
                <PlatformLayout />
              </PlatformProtectedRoute>
            }
          >
            <Route index element={<PlatformDashboard />} />
            {/* Overview */}
            <Route path="portfolio" element={<PortfolioHealth />} />
            <Route path="revenue" element={<PlatformRevenueDashboard />} />
            {/* Operations */}
            <Route path="tenants" element={<TenantRegistry />} />
            <Route path="tenants/:id" element={<TenantDrilldown />} />
            <Route path="subscriptions" element={<SubscriptionManagement />} />
            <Route path="events" element={<EventMonitor />} />
            {/* Intelligence */}
            <Route path="anomalies" element={<AnomalyCenter />} />
            <Route path="security" element={<SecurityIntelligence />} />
            <Route path="reports" element={<ReportsHub />} />
            <Route path="ai-insights" element={<AIInsights />} />
            {/* Governance */}
            <Route path="audit" element={<AuditTrail />} />
            <Route path="permissions" element={<PermissionCenter />} />
            {/* Infrastructure */}
            <Route path="settings" element={<PlatformSettings />} />
          </Route>

          {/* 🏠 RESIDENT PORTAL */}
          <Route
            path="/resident"
            element={
              <ProtectedRoute roles={["RESIDENT"]}>
                <ResidentLayout />
              </ProtectedRoute>
            }
          >
            <Route index               element={<ResidentPortal />}    />
            <Route path="bills"        element={<ResidentBills />}     />
            <Route path="payments"     element={<ResidentPayments />}  />
            <Route path="statements"   element={<ResidentStatements />}/>
            <Route path="notices"      element={<ResidentNotices />}   />
            <Route path="profile"      element={<ResidentProfile />}   />
          </Route>

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Home */}
            <Route index element={<Dashboard />} />
            <Route
              path="collections"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="PAYMENTS">
                  <CollectionsDashboard />
                </RoleProtectedRoute>
              }
            />

            {/* BLOCKS */}
            <Route
              path="blocks"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <Blocks />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="blocks/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <BlockCreate />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="blocks/:id"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <BlockView />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="blocks/:id/edit"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <BlockEdit />
                </RoleProtectedRoute>
              }
            />

            {/* FLATS */}
            <Route
              path="flats"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <Flats />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="flats/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <FlatCreate />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="flats/:id"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <FlatView />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="flats/:id/edit"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <FlatEdit />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/dashboard/admin/water-billing-rules"
              element={<WaterBillingRules />}
            />


            {/* OWNERS */}
            <Route
              path="owners"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <OwnerList />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="owners/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <OwnerCreate />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="owners/:id"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <OwnerView />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="owners/:id/edit"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <OwnerEdit />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="owners/:id/assign-flats"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <OwnerAssignFlats />
                </RoleProtectedRoute>
              }
            />

            {/* RESIDENTS */}
            <Route
              path="residents"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <ResidentsList />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="residents/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <ResidentCreate />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="residents/:id"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <ResidentView />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="residents/:id/edit"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <ResidentEdit />
                </RoleProtectedRoute>
              }
            />

            {/* SUPPLIERS */}
            <Route
              path="suppliers"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="SUPPLIERS">
                  <Suppliers />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="suppliers/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="SUPPLIERS" action="Write">
                  <SupplierCreate />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="suppliers/:id"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="SUPPLIERS">
                  <SupplierView />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="suppliers/:id/edit"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="SUPPLIERS" action="Write">
                  <SupplierEdit />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-purchases"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                  <WaterPurchases />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-purchases/entries/:id"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                  <WaterPurchaseEntryReview />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-purchases/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER" action="Write">
                  <WaterPurchaseCreate />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-purchases/:id"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                  <WaterPurchaseView />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-purchases/:id/edit"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER" action="Write">
                  <WaterPurchaseEdit />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-consumption"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                  <WaterConsumption />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-meters/:meterId/number"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                  <MeterNumberPage />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-consumption/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER" action="Write">
                  <WaterConsumptionForm />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-consumption/:id"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                  <WaterConsumptionView />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-consumption/:id/edit"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER" action="Write">
                  <WaterConsumptionForm />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="water-consumption/upload"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER" action="Write">
                <WaterConsumptionUpload />
                </RoleProtectedRoute>
              }
            />

                      {/* Water BILLS */}
                    
            <Route path="water-billing">
              <Route index element={<Navigate to="hub" replace />} />

              <Route
                path="hub"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                    <WaterBillingHub />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path=":month/:year"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                    <WaterBillingHub />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="new"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER" action="Write">
                    <WaterBillForm />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="edit/:id"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER" action="Write">
                    <WaterBillForm />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="generate"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER" action="Write">
                    <WaterSplitCockpit />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="summary/:id"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                    <WaterBillingSummaryPage />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path=":month/:year"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                    <WaterBillingReport />
                  </RoleProtectedRoute>
                }
              />
              <Route
                path="credits"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WATER">
                    <CreditNotes />
                  </RoleProtectedRoute>
                }
              />
            </Route>

   
       {/* =====================
            ELECTRICITY MODULE
           ===================== */}
        
        <Route
          path="/dashboard/electricity-bills/new"
          element={<RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="ELECTRICITY" action="Write">
          <ElectricityBillForm />
          </RoleProtectedRoute>}
        />

        <Route
          path="/dashboard/electricity-bills/:id"
          element={<RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="ELECTRICITY">
            <ElectricityBillView />
          </RoleProtectedRoute>}
        />

        <Route
          path="/dashboard/electricity-bills/:id/edit"
          element={<RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="ELECTRICITY" action="Write">
            <ElectricityBillForm />
          </RoleProtectedRoute>}
        />
      <Route
          path="/dashboard/electricity-bills"
          element={<RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="ELECTRICITY">
          <ElectricityBillList />
          </RoleProtectedRoute>}
        />

      <Route
        path="expenditures"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]} module="EXPENDITURE"><Expenditures/></RoleProtectedRoute>}
      />

      <Route
        path="expenditures/new"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]} module="EXPENDITURE" action="Write"><ExpenditureForm/></RoleProtectedRoute>}
      />

      <Route
        path="opening-balances"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]}><OpeningBalances/></RoleProtectedRoute>}
      />

      {OUTSTANDING_WRITEOFF_ENABLED && (
        <Route
          path="outstanding-writeoff"
          element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]}><OutstandingWriteOff/></RoleProtectedRoute>}
        />
      )}

      <Route
        path="budget"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]} module="EXPENDITURE" action="Write"><BudgetSettings/></RoleProtectedRoute>}
      />

      <Route
        path="expenditures/:id"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]} module="EXPENDITURE"><ExpenditureForm/></RoleProtectedRoute>}
      />

      <Route
        path="expenditures/recurring/new"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]} module="EXPENDITURE" action="Write"><RecurringExpenseForm/></RoleProtectedRoute>}
      />

      <Route
        path="expenditures/recurring/:id"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]} module="EXPENDITURE"><RecurringExpenseForm/></RoleProtectedRoute>}
      />

      <Route
        path="master-data/:entityType"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN"]} module="EXPENDITURE"><MasterDataManagementPage/></RoleProtectedRoute>}
      />

      {/* ── TREASURY ── */}
      <Route
        path="treasury"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR","COMMITTEE_MEMBER"]} module="TREASURY"><TreasuryDashboard/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/accounts"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR"]} module="TREASURY"><TreasuryAccounts/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/bank-accounts"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR"]} module="TREASURY"><TreasuryBankAccounts/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/bank-accounts/new"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT"]} module="TREASURY" action="Write"><BankAccountFormPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/bank-accounts/:id/edit"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT"]} module="TREASURY" action="Write"><BankAccountFormPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/cash-in-hand"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR"]} module="TREASURY"><TreasuryCashInHand/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/cash-in-hand/new"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT"]} module="TREASURY" action="Write"><CashAccountFormPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/cash-in-hand/:id/edit"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT"]} module="TREASURY" action="Write"><CashAccountFormPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/funds"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR"]} module="TREASURY"><TreasuryFunds/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/funds/new"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT"]} module="TREASURY" action="Write"><FundFormPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/funds/:id/edit"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT"]} module="TREASURY" action="Write"><FundFormPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/cashbook"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR"]} module="TREASURY"><CashbookPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/account-statements"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR","COMMITTEE_MEMBER"]} module="TREASURY"><AccountStatementsPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/verification-report"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR","COMMITTEE_MEMBER"]} module="TREASURY"><VerificationReportPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/accounts/:id/verify"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR","COMMITTEE_MEMBER"]} module="TREASURY" action="Write"><VerificationWizardPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/bank-reconciliation"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT"]} module="TREASURY"><BankReconciliationPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/bank-reconciliation/:id"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT"]} module="TREASURY"><BankReconciliationDetailPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/balance-sheet"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR","COMMITTEE_MEMBER"]} module="TREASURY"><BalanceSheetPage/></RoleProtectedRoute>}
      />
      <Route
        path="treasury/financial-audit"
        element={<RoleProtectedRoute allowed={["SUPER_ADMIN","TENANT_ADMIN","ACCOUNTANT","AUDITOR","COMMITTEE_MEMBER"]} module="TREASURY"><Suspense fallback={<AuditPageSkeleton />}><FinancialAuditPage/></Suspense></RoleProtectedRoute>}
      />

         {/* =====================
                  BILL STATEMENTS
              ===================== */}
              <Route
                path="billing-summary"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN", "RESIDENT"]} module="BILLING">
                    <BillingSummaryPage />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="billing-summary/:month/:year"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN", "RESIDENT"]} module="BILLING">
                    <BillingSummaryPage />
                  </RoleProtectedRoute>
                }
              />

              <Route
                path="bill-statements/:flatId"
                element={
                  <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN", "RESIDENT"]} module="BILLING">
                    <BillStatementView />
                  </RoleProtectedRoute>
                }
              />
         

        <Route
          path="/dashboard/billing-rules"
          element={<RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="BILLING"><BillingRulesList /></RoleProtectedRoute>}
        />

        <Route
          path="/dashboard/billing-rules/configure"
          element={<RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="BILLING" action="Write"><BillingRules /></RoleProtectedRoute>}
        />

        <Route
          path="/dashboard/email-config"
          element={
            <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WHATSAPP_CONFIG">
              <EmailSettings />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/dashboard/whatsapp-alerts"
          element={
            <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WHATSAPP_CONFIG">
              <WhatsAppSettings />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/dashboard/notification-preferences"
          element={
            <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]}>
              <NotificationPreferences />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/dashboard/billing-configuration"
          element={
            <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]}>
              <BillingConfiguration />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/dashboard/payment-activation"
          element={
            <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="WHATSAPP_CONFIG">
              <PaymentActivation />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/dashboard/roles-permissions"
          element={
            <RoleProtectedRoute allowed={["TENANT_ADMIN"]}>
              <RolesPermissions />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/dashboard/user-management"
          element={
            <RoleProtectedRoute allowed={["TENANT_ADMIN", "SOCIETY_MANAGER"]}>
              <ErrorBoundary>
                <UserManagement />
              </ErrorBoundary>
            </RoleProtectedRoute>
          }
        />

        <Route
          path="/dashboard/platform-revenue"
          element={
            <RoleProtectedRoute allowed={["SUPER_ADMIN"]}>
              <PlatformRevenueDashboard />
            </RoleProtectedRoute>
          }
        />

          {/* PAYMENTS */}
  <Route
    path="payments"
    element={
      <RoleProtectedRoute
        allowed={["SUPER_ADMIN", "TENANT_ADMIN", "RESIDENT"]}
        module="PAYMENTS"
      >
        <Payments />
      </RoleProtectedRoute>
    }
  />

  <Route
    path="payments/new"
    element={
      <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="PAYMENTS" action="Write">
        <PaymentUpload />
      </RoleProtectedRoute>
    }
  />

  {/* View payment */}
  <Route
    path="payments/:id"
    element={
      <RoleProtectedRoute
        allowed={["SUPER_ADMIN", "TENANT_ADMIN", "RESIDENT"]}
        module="PAYMENTS"
      >
        <PaymentView />
      </RoleProtectedRoute>
    }
  />

  {/* Edit payment */}
  <Route
    path="payments/:id/edit"
    element={
      <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="PAYMENTS" action="Write">
        <PaymentEdit />
      </RoleProtectedRoute>
    }
  />

         
            {/* RECEIPTS */}
            <Route
              path="receipts"
              element={
                <RoleProtectedRoute
                  allowed={["SUPER_ADMIN", "TENANT_ADMIN"]}
                  module="PAYMENTS"
                >
                  <Receipts />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="receipts/new"
              element={
                <RoleProtectedRoute
                  allowed={["SUPER_ADMIN", "TENANT_ADMIN"]}
                  module="PAYMENTS"
                  action="Write"
                >
                  <ReceiptCreate />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="receipts/:id"
              element={
                <RoleProtectedRoute
                  allowed={["SUPER_ADMIN", "TENANT_ADMIN", "RESIDENT"]}
                  module="PAYMENTS"
                >
                  <ReceiptsView />
                </RoleProtectedRoute>
              }
            />


            <Route
              path="reports/ledger"
              element={
                <RoleProtectedRoute
                  allowed={["SUPER_ADMIN", "TENANT_ADMIN"]}
                  module="REPORTS"
                >
                  <LedgerReport />
                </RoleProtectedRoute>
              }
            />

            <Route
              path="reports/financial-health"
              element={
                <RoleProtectedRoute
                  allowed={["SUPER_ADMIN", "TENANT_ADMIN"]}
                  module="REPORTS"
                >
                  <FinancialHealthDashboard />
                </RoleProtectedRoute>
              }
            />


            {/* STAFF */}
            <Route
              path="staff"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <Staff />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="staff/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <StaffCreate />
                </RoleProtectedRoute>
              }
            />

            {/* NOTICES */}
            <Route
              path="notices"
              element={
                <RoleProtectedRoute
                  allowed={["SUPER_ADMIN", "TENANT_ADMIN", "RESIDENT"]}
                  module="NOTICES"
                >
                  <Notices />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="notices/new"
              element={
                <RoleProtectedRoute
                  allowed={["SUPER_ADMIN", "TENANT_ADMIN"]}
                  module="NOTICES"
                  action="Write"
                >
                  <NoticeCreate />
                </RoleProtectedRoute>
              }
            />

            {/* LEADS & VACANCIES */}
            <Route
              path="vacancies"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <VacancyHub />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="leads"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS">
                  <LeadManagement />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="leads/new"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="RESIDENTS" action="Write">
                  <LeadCreate />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="smart-import"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="MIGRATIONS">
                  <SmartImportStudio />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="smart-import/:sessionId/summary"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="MIGRATIONS">
                  <ImportSummaryPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="smart-import/history"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="MIGRATIONS">
                  <ImportHistoryPage />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="communication-logs"
              element={
                <RoleProtectedRoute allowed={["SUPER_ADMIN", "TENANT_ADMIN"]} module="REPORTS">
                  <CommunicationLogs />
                </RoleProtectedRoute>
              }
            />

            {/* MY PROFILE — accessible to all logged-in dashboard users */}
            <Route path="profile" element={<ProfilePage />} />
          </Route>
        </Routes>
        </Suspense>
      </Router>
      </FormDraftProvider>
    </ToastProvider>
  );
}

export default App;
